import * as Sentry from '@sentry/react';

const lastTs: Record<string, number> = {};

export interface ReportThrottledOptions {
  /** Throttle window in milliseconds. Events with the same key fire at most once per window. */
  throttleMs: number;
  level?: Sentry.SeverityLevel;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  tags?: Record<string, string>;
}

/**
 * Throttled Sentry.captureMessage. Drops calls that arrive within `throttleMs`
 * of the last accepted call for the same `key`.
 *
 * Convention: prefix keys with a component or feature name to avoid cross-call-site
 * collisions, e.g. 'webgl-heatmap:context-restored'.
 */
export const reportThrottled = (
  key: string,
  message: string,
  options: ReportThrottledOptions,
): boolean => {
  const now = Date.now();
  if (now - (lastTs[key] ?? 0) < options.throttleMs) {
    return false;
  }
  lastTs[key] = now;

  Sentry.captureMessage(message, {
    level: options.level ?? 'error',
    tags: { site: key, ...options.tags },
    ...(options.extra ? { extra: options.extra } : {}),
    ...(options.contexts ? { contexts: options.contexts } : {}),
  });
  return true;
};

/** For test cleanup. Resets all throttle windows. */
export const __resetSentryThrottle = () => {
  for (const k of Object.keys(lastTs)) {
    Reflect.deleteProperty(lastTs, k);
  }
};
